import React, { useEffect, useState } from "react";

const STORAGE_KEY = "addToHomeScreenDismissed";

const isMobile = () => {
  if (typeof window === "undefined") return false;
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent
  );
};

const isIOS = () =>
  /iPhone|iPad|iPod/i.test(navigator.userAgent);

const isStandalone = () => {
  if (typeof window === "undefined") return true;
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
};

/**
 * Small banner prompting mobile users to add the app to home screen for notifications.
 * - iOS: Shows custom instructions (Share > Add to Home Screen)
 * - Android: Uses native beforeinstallprompt when available, else instructions
 */
const AddToHomeScreenBanner = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showBanner, setShowBanner] = useState(false);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (!isMobile() || isStandalone()) return;
    if (sessionStorage.getItem(STORAGE_KEY) === "true") return;

    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowBanner(true);
    };

    window.addEventListener("beforeinstallprompt", handler);

    // iOS: beforeinstallprompt never fires — show after short delay
    // Android (no prompt): fallback if beforeinstallprompt doesn't fire
    const timer = setTimeout(() => setShowBanner(true), isIOS() ? 1200 : 2500);

    return () => {
      window.removeEventListener("beforeinstallprompt", handler);
      clearTimeout(timer);
    };
  }, []);

  const handleInstall = () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      deferredPrompt.userChoice.then((choiceResult) => {
        setDeferredPrompt(null);
        setShowBanner(false);
      });
    } else {
      setExpanded(true);
    }
  };

  const handleClose = () => {
    sessionStorage.setItem(STORAGE_KEY, "true");
    setShowBanner(false);
    setExpanded(false);
  };

  if (!showBanner) return null;

  return (
    <div
      className="fixed bottom-4 left-3 right-3 z-[9997] max-w-md mx-auto sm:left-auto sm:right-4"
      role="dialog"
      aria-label="Ajouter à l'écran d'accueil"
    >
      <div className="rounded-xl border-2 border-brand/25 bg-white shadow-elevated overflow-hidden">
        <div className="flex items-center gap-3 px-4 py-3">
          <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-brand/15 flex items-center justify-center text-lg">
            📲
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-gray-900 text-sm">
              Ajoutez l'app pour recevoir des notifications
            </p>
            <p className="text-gray-500 text-xs mt-0.5">
              Commandes prêtes, repas planifiés, et plus.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={handleInstall}
              className="py-2 px-3 rounded-lg bg-brand hover:bg-brand-dark text-white font-medium text-sm transition-colors"
            >
              {deferredPrompt ? "Installer" : isIOS() ? "Comment ?" : "Ajouter"}
            </button>
            <button
              onClick={handleClose}
              className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
              aria-label="Fermer"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Expanded instructions for iOS (or Android without prompt) */}
        {expanded && (
          <div className="border-t border-gray-100 bg-gray-50 px-4 py-3 text-xs text-gray-600">
            {isIOS() ? (
              <>
                <p className="font-medium text-gray-700 mb-1">Sur iPhone / iPad :</p>
                <ol className="list-decimal list-inside space-y-0.5">
                  <li>Appuyez sur <strong>Partager</strong> (icône en bas de l'écran)</li>
                  <li>Faites défiler et choisissez <strong>« Sur l'écran d'accueil »</strong></li>
                  <li>Appuyez sur <strong>Ajouter</strong></li>
                </ol>
              </>
            ) : (
              <>
                <p className="font-medium text-gray-700 mb-1">Sur Android :</p>
                <ol className="list-decimal list-inside space-y-0.5">
                  <li>Appuyez sur le menu ⋮ (en haut à droite)</li>
                  <li>Choisissez <strong>« Ajouter à l'écran d'accueil »</strong> ou <strong>« Installer l'application »</strong></li>
                </ol>
              </>
            )}
            <button
              onClick={handleClose}
              className="mt-2 text-brand font-medium hover:underline"
            >
              Compris
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AddToHomeScreenBanner;
